/**
 * The actor-critic network, written out longhand.
 *
 * A shared tanh trunk feeds a linear policy head (logits over the action table)
 * and a linear value head (one scalar). Everything lives in ONE flat
 * `Float64Array` of parameters, with the layers writing into disjoint slices of
 * it. That layout is not an optimization, it is what makes the rest of the
 * stack tractable:
 *
 *   - checkpointing is a copy of one array,
 *   - Adam's moments are two arrays of the same shape,
 *   - global gradient-norm clipping is one pass,
 *   - and finite-difference gradient checking can perturb parameter `i`
 *     without knowing or caring which layer it belongs to. That last one is
 *     the whole reason this file is hand-written rather than a framework: the
 *     gradients are checked against central differences to a stated relative
 *     tolerance, which is a real test rather than trust.
 *
 * `Float64Array` throughout for parameters, activations and gradients.
 * Float32 would halve the memory and would also put the gradient check's noise
 * floor at roughly 1e-3, which is above the tolerance we want to assert at.
 * Observations arrive as Float32Array (that is the wire format from the
 * environment) and are widened on the way in.
 *
 * ---------------------------------------------------------------------------
 * INITIALIZATION
 * ---------------------------------------------------------------------------
 *
 * Semi-orthogonal init, seeded, with per-layer gains:
 *
 *   trunk layers  gain sqrt(2)
 *   policy head   gain 0.01
 *   value head    gain 1.0
 *
 * Orthogonal rather than plain scaled-normal because an orthogonal matrix
 * preserves the norm of its input exactly, so activations neither shrink nor
 * blow up as depth grows, and it is what every reference PPO implementation
 * uses. The gains follow the same references.
 *
 * The 0.01 on the policy head is the one that matters most and is the least
 * obvious. The head's output is logits; a softmax over logits whose spread is
 * large is nearly a one-hot, so a policy initialized with a unit-gain head
 * starts out almost deterministic, has near-zero entropy, and only ever sees
 * the consequences of the one action it already prefers. It cannot explore its
 * way out of that, because the entropy bonus has to fight a saturated softmax.
 * With gain 0.01 the initial logits are within about +/-0.03 of each other, so
 * the starting policy is uniform to three decimal places and every action gets
 * tried. The value head has no such constraint — its output is a regression
 * target, so unit gain is right.
 *
 * Biases start at zero. There is no reason to break symmetry in the bias when
 * the weights already do it.
 */

import { Rng } from '../physics/rng';
import type {
  Checkpoint,
  ForwardResult,
  Observation,
  PolicyNetwork,
  TrainingStats,
} from './types';
import { CHECKPOINT_VERSION } from './types';

// ---------------------------------------------------------------------------
// Numerically stable softmax
// ---------------------------------------------------------------------------

/**
 * log(sum(exp(logits))), computed by factoring out the maximum.
 *
 * The naive version overflows to Infinity for logits around 710 and underflows
 * to 0 (log -> -Infinity) for logits around -746. Neither is exotic: a policy
 * whose head has drifted, or a value head fed an unnormalized observation, gets
 * there in a few thousand updates, and the resulting NaN propagates into every
 * parameter on the next backward pass with nothing in the stack trace to say
 * where it started. Subtracting the max makes the largest term exactly exp(0)
 * and bounds the rest in (0, 1], so the sum is in [1, n] for any input.
 */
export const logSumExp = (logits: Float64Array, offset = 0, count = logits.length): number => {
  let max = -Infinity;
  for (let i = 0; i < count; i++) {
    const v = logits[offset + i];
    if (v > max) max = v;
  }
  // All -Infinity is degenerate but must not produce NaN from (-inf) - (-inf).
  if (max === -Infinity) return -Infinity;
  let sum = 0;
  for (let i = 0; i < count; i++) sum += Math.exp(logits[offset + i] - max);
  return max + Math.log(sum);
};

/** Stable softmax. `out` may alias `logits`. */
export const softmax = (logits: Float64Array, out: Float64Array): Float64Array => {
  const n = logits.length;
  let max = -Infinity;
  for (let i = 0; i < n; i++) if (logits[i] > max) max = logits[i];
  let sum = 0;
  for (let i = 0; i < n; i++) {
    const e = Math.exp(logits[i] - max);
    out[i] = e;
    sum += e;
  }
  const inv = 1 / sum;
  for (let i = 0; i < n; i++) out[i] *= inv;
  return out;
};

/**
 * Stable log-softmax. `out` may alias `logits`.
 *
 * Computed as `z - logSumExp(z)` rather than `log(softmax(z))`: the latter
 * takes the log of a probability that has already been rounded, so an action
 * with probability 1e-320 comes back as -Infinity instead of about -737, and
 * PPO's ratio `exp(newLogProb - oldLogProb)` becomes NaN rather than merely
 * small.
 */
export const logSoftmax = (logits: Float64Array, out: Float64Array): Float64Array => {
  const lse = logSumExp(logits);
  for (let i = 0; i < logits.length; i++) out[i] = logits[i] - lse;
  return out;
};

/** Shannon entropy of a categorical distribution given its LOG probabilities. */
export const entropyOfLogProbs = (logProbs: Float64Array): number => {
  let h = 0;
  for (let i = 0; i < logProbs.length; i++) {
    const p = Math.exp(logProbs[i]);
    // p * log p -> 0 as p -> 0; the multiplication would give 0 * -Infinity.
    if (p > 0) h -= p * logProbs[i];
  }
  return h;
};

/** Draw an action index from a normalized probability vector. */
export const sampleCategorical = (probs: Float64Array, rng: Rng): number => {
  const u = rng.next();
  let acc = 0;
  for (let i = 0; i < probs.length; i++) {
    acc += probs[i];
    if (u < acc) return i;
  }
  // Only reachable through floating-point slack in the sum.
  return probs.length - 1;
};

// ---------------------------------------------------------------------------
// Seeded Gaussian
// ---------------------------------------------------------------------------

/**
 * One standard normal from the shared uniform generator, by Box-Muller.
 *
 * The second variate the transform produces is discarded rather than cached:
 * caching would make the value returned for a given generator state depend on
 * how many times the function had been called before, and initialization would
 * stop being reproducible from the seed alone.
 */
const nextGaussian = (rng: Rng): number => {
  let u1 = rng.next();
  // log(0) is -Infinity; nudge off the open end of the interval.
  if (u1 <= 0) u1 = Number.MIN_VALUE;
  const u2 = rng.next();
  return Math.sqrt(-2 * Math.log(u1)) * Math.cos(2 * Math.PI * u2);
};

/**
 * Fill `out[offset .. offset + rows*cols)` (row-major) with a semi-orthogonal
 * matrix scaled by `gain`.
 *
 * Modified Gram-Schmidt on a Gaussian matrix. When rows >= cols the columns are
 * orthonormalized (so W^T W = gain^2 I); otherwise the transpose is
 * orthonormalized, which is the best available when the matrix cannot have
 * orthonormal columns at all. Modified rather than classical Gram-Schmidt
 * because the classical form loses orthogonality catastrophically for
 * ill-conditioned inputs, and a random matrix with a few hundred columns is
 * quite capable of being ill-conditioned.
 */
const orthogonalInit = (
  out: Float64Array,
  offset: number,
  rows: number,
  cols: number,
  gain: number,
  rng: Rng,
): void => {
  const n = Math.max(rows, cols);
  const m = Math.min(rows, cols);
  // Column-major n x m scratch, so a column is contiguous.
  const a = new Float64Array(n * m);
  for (let i = 0; i < a.length; i++) a[i] = nextGaussian(rng);

  for (let c = 0; c < m; c++) {
    const base = c * n;
    for (let p = 0; p < c; p++) {
      const pbase = p * n;
      let d = 0;
      for (let r = 0; r < n; r++) d += a[pbase + r] * a[base + r];
      for (let r = 0; r < n; r++) a[base + r] -= d * a[pbase + r];
    }
    let norm = 0;
    for (let r = 0; r < n; r++) norm += a[base + r] * a[base + r];
    norm = Math.sqrt(norm);
    // A Gaussian matrix is full rank with probability 1; the guard is for the
    // measure-zero case rather than an expected path.
    const inv = norm > 1e-12 ? 1 / norm : 0;
    for (let r = 0; r < n; r++) a[base + r] *= inv;
  }

  for (let r = 0; r < rows; r++) {
    for (let c = 0; c < cols; c++) {
      out[offset + r * cols + c] = gain * (rows >= cols ? a[c * n + r] : a[r * n + c]);
    }
  }
};

// ---------------------------------------------------------------------------
// The network
// ---------------------------------------------------------------------------

/** Gain applied to each trunk layer. sqrt(2) is the reference PPO value. */
export const TRUNK_GAIN = Math.SQRT2;
/** Gain on the policy head. Small on purpose — see the header. */
export const POLICY_HEAD_GAIN = 0.01;
/** Gain on the value head. Its output is a regression target, not a logit. */
export const VALUE_HEAD_GAIN = 1;

/** Where one dense layer's weights and bias live in the flat parameter array. */
interface LayerSpec {
  inSize: number;
  outSize: number;
  /** Start of the row-major outSize x inSize weight block. */
  weightOffset: number;
  /** Start of the outSize bias block. */
  biasOffset: number;
}

/** Allocate a `ForwardResult` sized for a given action count. */
export const makeForwardResult = (actionCount: number): ForwardResult => ({
  logits: new Float64Array(actionCount),
  value: 0,
});

/**
 * Output rows handled together by the dense kernels below. See the note on
 * `applyLinear` for why this is four and not one.
 */
const ROW_BLOCK = 4;

export class MlpPolicy implements PolicyNetwork {
  readonly obsSize: number;
  readonly actionCount: number;
  readonly hiddenSizes: readonly number[];
  readonly parameterCount: number;

  /** Everything the network knows, in one contiguous block. */
  private readonly params: Float64Array;
  /** Gradient of the current loss w.r.t. `params`, element for element. */
  private readonly grads: Float64Array;

  /** Trunk layers, then the policy head, then the value head. */
  private readonly trunk: LayerSpec[] = [];
  private readonly policyHead: LayerSpec;
  private readonly valueHead: LayerSpec;

  /**
   * `acts[0]` is the widened observation; `acts[i+1]` is trunk layer i's tanh
   * output. Kept across calls so `forward` allocates nothing and so `backward`
   * has the activations it needs without a second forward pass.
   */
  private readonly acts: Float64Array[] = [];
  /** Per-layer backward scratch, same shapes as `acts`. */
  private readonly deltas: Float64Array[] = [];

  constructor(obsSize: number, actionCount: number, hiddenSizes: readonly number[], seed = 0) {
    if (obsSize <= 0) throw new Error(`obsSize must be positive, got ${obsSize}`);
    if (actionCount <= 0) throw new Error(`actionCount must be positive, got ${actionCount}`);
    this.obsSize = obsSize;
    this.actionCount = actionCount;
    this.hiddenSizes = [...hiddenSizes];

    let cursor = 0;
    const layer = (inSize: number, outSize: number): LayerSpec => {
      const spec: LayerSpec = {
        inSize,
        outSize,
        weightOffset: cursor,
        biasOffset: cursor + outSize * inSize,
      };
      cursor += outSize * inSize + outSize;
      return spec;
    };

    let width = obsSize;
    for (const h of this.hiddenSizes) {
      if (h <= 0) throw new Error(`hidden layer size must be positive, got ${h}`);
      this.trunk.push(layer(width, h));
      width = h;
    }
    this.policyHead = layer(width, actionCount);
    this.valueHead = layer(width, 1);

    this.parameterCount = cursor;
    this.params = new Float64Array(cursor);
    this.grads = new Float64Array(cursor);

    this.acts.push(new Float64Array(obsSize));
    this.deltas.push(new Float64Array(obsSize));
    for (const h of this.hiddenSizes) {
      this.acts.push(new Float64Array(h));
      this.deltas.push(new Float64Array(h));
    }

    this.initialize(seed);
  }

  /** Re-roll every weight from `seed`. Biases go to zero. */
  initialize(seed: number): void {
    const rng = new Rng(seed);
    this.params.fill(0);
    for (const spec of this.trunk) {
      orthogonalInit(this.params, spec.weightOffset, spec.outSize, spec.inSize, TRUNK_GAIN, rng);
    }
    orthogonalInit(
      this.params,
      this.policyHead.weightOffset,
      this.actionCount,
      this.policyHead.inSize,
      POLICY_HEAD_GAIN,
      rng,
    );
    orthogonalInit(
      this.params,
      this.valueHead.weightOffset,
      1,
      this.valueHead.inSize,
      VALUE_HEAD_GAIN,
      rng,
    );
  }

  // -------------------------------------------------------------------------
  // Forward
  // -------------------------------------------------------------------------

  /**
   * Logits and value for one observation.
   *
   * Allocates nothing: the activations live in `this.acts` and the outputs are
   * written into the caller's `out`. This runs once per agent per tick during
   * rollouts, so an allocation here is a garbage-collection pause in the middle
   * of a training run.
   *
   * The activations are left in place afterwards, and `backward` consumes them.
   */
  forward(obs: Observation, out: ForwardResult): ForwardResult {
    return this.forwardAt(obs, 0, out);
  }

  /** As `forward`, reading `obsSize` values starting at `offset`. For batches
   *  whose observations are packed end to end in one array. */
  forwardAt(obs: Float32Array | Float64Array, offset: number, out: ForwardResult): ForwardResult {
    const input = this.acts[0];
    for (let i = 0; i < this.obsSize; i++) input[i] = obs[offset + i];

    for (let l = 0; l < this.trunk.length; l++) {
      const spec = this.trunk[l];
      const dst = this.acts[l + 1];
      this.applyLinear(spec, this.acts[l], dst, 0);
      // The activation is a separate pass over `outSize` doubles that are
      // already in L1. Fusing it into the kernel would cost the kernel a
      // branch on whether this layer has one; storing the sum and reading it
      // straight back is exact, so the two forms agree bit for bit.
      for (let j = 0; j < spec.outSize; j++) dst[j] = Math.tanh(dst[j]);
    }

    const last = this.acts[this.trunk.length];
    this.applyLinear(this.policyHead, last, out.logits, 0);
    out.value = this.linearScalar(this.valueHead, last);
    return out;
  }

  /**
   * `dst = W src + b`, FOUR OUTPUT ROWS AT A TIME.
   *
   * A dense layer is a stack of independent dot products, and the obvious loop
   * — one row, one accumulator — makes each one a serial chain of
   * floating-point adds. An add has several cycles of latency and there is
   * nothing else in flight to fill them, so the core spends most of the layer
   * waiting on the previous add rather than on memory or on the multiply.
   * Four rows at once gives four independent chains, and loads `src[k]` once
   * instead of four times. Measured on the shipped 256x256 trunk layer that is
   * about 1.3x for this kernel and 1.5x for its backward counterpart.
   *
   * THIS IS NOT A CHANGE OF ARITHMETIC. Each accumulator still starts at its
   * own bias and still sums its own row in ascending `k`, so every output is
   * the same sequence of operations on the same values and is bit-identical to
   * the one-row form. That property matters more here than the speed: the
   * trainer's tests compare a worker run against a sequential one parameter by
   * parameter, and a re-associated sum would break them while looking like
   * nothing worse than rounding.
   */
  private applyLinear(
    spec: LayerSpec,
    src: Float64Array,
    dst: Float64Array,
    dstOffset: number,
  ): void {
    const p = this.params;
    const inSize = spec.inSize;
    const outSize = spec.outSize;
    const weights = spec.weightOffset;
    const biases = spec.biasOffset;

    let j = 0;
    for (; j + ROW_BLOCK <= outSize; j += ROW_BLOCK) {
      const r0 = weights + j * inSize;
      const r1 = r0 + inSize;
      const r2 = r1 + inSize;
      const r3 = r2 + inSize;
      let s0 = p[biases + j];
      let s1 = p[biases + j + 1];
      let s2 = p[biases + j + 2];
      let s3 = p[biases + j + 3];
      for (let k = 0; k < inSize; k++) {
        const v = src[k];
        s0 += p[r0 + k] * v;
        s1 += p[r1 + k] * v;
        s2 += p[r2 + k] * v;
        s3 += p[r3 + k] * v;
      }
      dst[dstOffset + j] = s0;
      dst[dstOffset + j + 1] = s1;
      dst[dstOffset + j + 2] = s2;
      dst[dstOffset + j + 3] = s3;
    }
    // The tail. A layer whose width is not a multiple of four — the 62-wide
    // policy head, say — still has to produce its last rows.
    for (; j < outSize; j++) {
      let sum = p[biases + j];
      const row = weights + j * inSize;
      for (let k = 0; k < inSize; k++) sum += p[row + k] * src[k];
      dst[dstOffset + j] = sum;
    }
  }

  private linearScalar(spec: LayerSpec, src: Float64Array): number {
    const p = this.params;
    let sum = p[spec.biasOffset];
    const row = spec.weightOffset;
    for (let k = 0; k < spec.inSize; k++) sum += p[row + k] * src[k];
    return sum;
  }

  // -------------------------------------------------------------------------
  // Backward
  // -------------------------------------------------------------------------

  /**
   * Accumulate dLoss/dParameter given dLoss/dLogits and dLoss/dValue.
   *
   * MUST be called immediately after a `forward`/`forwardAt` for the same
   * observation: it reuses that call's cached activations rather than
   * recomputing them, which halves the cost of a training step. Calling it
   * after a forward for a DIFFERENT observation silently produces the gradient
   * of a mixture of the two, so the pairing is the caller's contract.
   *
   * Accumulates rather than assigns, so a minibatch is a loop of
   * forward/backward pairs over a single `zeroGradients()`.
   */
  backward(dLogits: Float64Array, dValue: number): void {
    const g = this.grads;
    const p = this.params;
    const last = this.acts[this.trunk.length];
    const dLast = this.deltas[this.trunk.length];
    dLast.fill(0);

    // Policy head: y = W x + b, so dW = dy x^T, db = dy, dx = W^T dy. Its
    // deltas arrive ready to use, hence the null activation.
    this.accumulateLayer(this.policyHead, last, dLogits, null, dLast);

    // Value head: identical, with a single output row — there is nothing for
    // the blocked kernel to block, so it stays written out.
    if (dValue !== 0) {
      const spec = this.valueHead;
      const row = spec.weightOffset;
      for (let k = 0; k < spec.inSize; k++) {
        g[row + k] += dValue * last[k];
        dLast[k] += p[row + k] * dValue;
      }
      g[spec.biasOffset] += dValue;
    }

    // Trunk, back to front. d/dz tanh(z) = 1 - tanh(z)^2, and the activation
    // IS tanh(z), so the pre-activation never has to be stored.
    for (let l = this.trunk.length - 1; l >= 0; l--) {
      const dIn = this.deltas[l];
      dIn.fill(0);
      this.accumulateLayer(
        this.trunk[l],
        this.acts[l],
        this.deltas[l + 1],
        this.acts[l + 1],
        dIn,
      );
    }
  }

  /**
   * One layer's contribution to `grads`, and the gradient it hands back to the
   * layer below. Blocked four output rows at a time for the reason given on
   * `applyLinear`; `act`, when given, is that layer's tanh output, so the
   * kernel can turn a gradient w.r.t. the activation into one w.r.t. the
   * pre-activation without a second buffer.
   *
   * BOTH ACCUMULATIONS KEEP THEIR ORIGINAL ORDER. `grads[row + k]` still sums
   * over samples in the order the caller supplies them — this function only
   * ever adds one term to it — and `dIn[k]` still sums over output rows in
   * ascending `j`, because the block loop walks `j` upward and the four adds
   * inside it are written out in order rather than folded into one expression.
   * The running `dIn[k]` is held in a local across those four so it is read and
   * written once instead of four times, which is where most of the backward
   * pass's speed-up comes from.
   */
  private accumulateLayer(
    spec: LayerSpec,
    src: Float64Array,
    dOut: Float64Array,
    act: Float64Array | null,
    dIn: Float64Array,
  ): void {
    const g = this.grads;
    const p = this.params;
    const inSize = spec.inSize;
    const outSize = spec.outSize;
    const weights = spec.weightOffset;
    const biases = spec.biasOffset;

    let j = 0;
    for (; j + ROW_BLOCK <= outSize; j += ROW_BLOCK) {
      const d0 = act === null ? dOut[j] : dOut[j] * (1 - act[j] * act[j]);
      const d1 = act === null ? dOut[j + 1] : dOut[j + 1] * (1 - act[j + 1] * act[j + 1]);
      const d2 = act === null ? dOut[j + 2] : dOut[j + 2] * (1 - act[j + 2] * act[j + 2]);
      const d3 = act === null ? dOut[j + 3] : dOut[j + 3] * (1 - act[j + 3] * act[j + 3]);
      // A row whose delta is zero contributes `0 * x`, and adding that to an
      // accumulator leaves it exactly as it was — so skipping a row and
      // running it are the same answer, and only a wholly dead block is worth
      // the branch. The all-zero case is not hypothetical: PPO differentiates
      // the two heads separately, and a value-only step hands this function a
      // dLogits of nothing but zeros.
      if (d0 === 0 && d1 === 0 && d2 === 0 && d3 === 0) continue;
      const r0 = weights + j * inSize;
      const r1 = r0 + inSize;
      const r2 = r1 + inSize;
      const r3 = r2 + inSize;
      for (let k = 0; k < inSize; k++) {
        const s = src[k];
        g[r0 + k] += d0 * s;
        g[r1 + k] += d1 * s;
        g[r2 + k] += d2 * s;
        g[r3 + k] += d3 * s;
        let d = dIn[k];
        d += p[r0 + k] * d0;
        d += p[r1 + k] * d1;
        d += p[r2 + k] * d2;
        d += p[r3 + k] * d3;
        dIn[k] = d;
      }
      g[biases + j] += d0;
      g[biases + j + 1] += d1;
      g[biases + j + 2] += d2;
      g[biases + j + 3] += d3;
    }
    for (; j < outSize; j++) {
      const d = act === null ? dOut[j] : dOut[j] * (1 - act[j] * act[j]);
      if (d === 0) continue;
      const row = weights + j * inSize;
      for (let k = 0; k < inSize; k++) {
        g[row + k] += d * src[k];
        dIn[k] += p[row + k] * d;
      }
      g[biases + j] += d;
    }
  }

  // -------------------------------------------------------------------------
  // Parameters and gradients
  // -------------------------------------------------------------------------

  /** The live parameter buffer, not a copy. Adam writes through it, and the
   *  gradient check perturbs it in place. */
  getParameters(): Float64Array {
    return this.params;
  }

  setParameters(values: Float64Array | readonly number[]): void {
    if (values.length !== this.parameterCount) {
      throw new Error(
        `parameter count mismatch: network has ${this.parameterCount}, got ${values.length}`,
      );
    }
    // `set` rather than a loop: it is one copy for a typed array and the same
    // element-by-element conversion for a plain one, so the result is identical
    // either way. It is on the hot path of the parallel gradient update, where
    // every shard re-reads the parameter vector once per minibatch.
    this.params.set(values as ArrayLike<number>);
  }

  /** The live gradient buffer, parallel to `getParameters()`. */
  getGradients(): Float64Array {
    return this.grads;
  }

  zeroGradients(): void {
    this.grads.fill(0);
  }

  /** Independent copy, sharing nothing. Optimizer state is NOT copied. */
  clone(): MlpPolicy {
    const copy = new MlpPolicy(this.obsSize, this.actionCount, this.hiddenSizes, 0);
    copy.setParameters(this.params);
    return copy;
  }
}

// ---------------------------------------------------------------------------
// Gradient clipping
// ---------------------------------------------------------------------------

/** L2 norm of a flat vector. */
export const l2Norm = (values: Float64Array): number => {
  let sum = 0;
  for (let i = 0; i < values.length; i++) sum += values[i] * values[i];
  return Math.sqrt(sum);
};

/**
 * Scale `grads` in place so its global L2 norm is at most `maxNorm`. Returns
 * the norm BEFORE clipping, which is the number worth logging: a run whose
 * gradient norm is pinned at the clip threshold every step is being throttled,
 * not stabilized.
 *
 * Global, over every parameter at once, rather than per layer — scaling all
 * parameters by one factor preserves the direction of the update exactly, and
 * only its length changes. Per-layer clipping would rotate it.
 */
export const clipGradNorm = (grads: Float64Array, maxNorm: number): number => {
  const norm = l2Norm(grads);
  if (norm > maxNorm && norm > 0) {
    const scale = maxNorm / norm;
    for (let i = 0; i < grads.length; i++) grads[i] *= scale;
  }
  return norm;
};

// ---------------------------------------------------------------------------
// Adam
// ---------------------------------------------------------------------------

/** Adam's moments, in the JSON-safe shape `Checkpoint.optimizer` wants. */
export interface AdamState {
  m: number[];
  v: number[];
  step: number;
}

export interface AdamOptions {
  learningRate?: number;
  beta1?: number;
  beta2?: number;
  epsilon?: number;
}

/**
 * Adam, with bias correction.
 *
 * The bias correction is not a detail. `m` and `v` both start at zero, so
 * before correction the first update is scaled by (1 - beta1) = 0.1 on the
 * numerator and sqrt(1 - beta2) = 0.0316 on the denominator, i.e. the first
 * step is about 3x too LARGE in the numerator-to-denominator ratio while both
 * estimates are still mostly zero. Corrected, the first step's magnitude is
 * almost exactly the learning rate regardless of the gradient's size, which is
 * the property the rest of the hyperparameters are tuned against. Skipping it
 * gives a run that trains anyway, just with an effective learning rate that
 * differs from the configured one for the first few hundred steps — the kind of
 * bug that is never noticed and quietly costs a percentage of final score.
 */
export class Adam {
  learningRate: number;
  readonly beta1: number;
  readonly beta2: number;
  readonly epsilon: number;
  /** Updates applied so far. Drives the bias correction, so it must survive a
   *  checkpoint or a resumed run restarts the correction from scratch. */
  step = 0;

  private readonly m: Float64Array;
  private readonly v: Float64Array;

  constructor(size: number, options: AdamOptions = {}) {
    this.learningRate = options.learningRate ?? 3e-4;
    this.beta1 = options.beta1 ?? 0.9;
    this.beta2 = options.beta2 ?? 0.999;
    this.epsilon = options.epsilon ?? 1e-8;
    this.m = new Float64Array(size);
    this.v = new Float64Array(size);
  }

  get size(): number {
    return this.m.length;
  }

  /** One step of `params -= lr * mHat / (sqrt(vHat) + eps)`. */
  update(params: Float64Array, grads: Float64Array): void {
    if (params.length !== this.m.length || grads.length !== this.m.length) {
      throw new Error(
        `Adam sized for ${this.m.length}, got params ${params.length} / grads ${grads.length}`,
      );
    }
    this.step++;
    const b1 = this.beta1;
    const b2 = this.beta2;
    const correction1 = 1 - Math.pow(b1, this.step);
    const correction2 = 1 - Math.pow(b2, this.step);
    const lr = this.learningRate;
    for (let i = 0; i < params.length; i++) {
      const g = grads[i];
      const m = b1 * this.m[i] + (1 - b1) * g;
      const v = b2 * this.v[i] + (1 - b2) * g * g;
      this.m[i] = m;
      this.v[i] = v;
      params[i] -= (lr * (m / correction1)) / (Math.sqrt(v / correction2) + this.epsilon);
    }
  }

  reset(): void {
    this.m.fill(0);
    this.v.fill(0);
    this.step = 0;
  }

  /** JSON-safe snapshot. */
  exportState(): AdamState {
    return { m: Array.from(this.m), v: Array.from(this.v), step: this.step };
  }

  importState(state: AdamState): void {
    if (state.m.length !== this.m.length || state.v.length !== this.v.length) {
      throw new Error(
        `optimizer state sized ${state.m.length}, expected ${this.m.length}`,
      );
    }
    for (let i = 0; i < this.m.length; i++) {
      this.m[i] = state.m[i];
      this.v[i] = state.v[i];
    }
    this.step = state.step;
  }
}

// ---------------------------------------------------------------------------
// Checkpoints
// ---------------------------------------------------------------------------

/** Bundle a network and its optimizer into the JSON-safe checkpoint shape. */
export const toCheckpoint = (
  net: MlpPolicy,
  adam: Adam | null,
  stats: TrainingStats,
  label?: string,
): Checkpoint => {
  const checkpoint: Checkpoint = {
    version: CHECKPOINT_VERSION,
    obsSize: net.obsSize,
    actionCount: net.actionCount,
    hiddenSizes: [...net.hiddenSizes],
    parameters: Array.from(net.getParameters()),
    stats,
  };
  if (adam) checkpoint.optimizer = adam.exportState();
  if (label !== undefined) checkpoint.label = label;
  return checkpoint;
};

/**
 * Restore a checkpoint into an existing network and optimizer.
 *
 * Every guard here throws rather than coerces. A checkpoint whose observation
 * layout or action table differs from the running one describes a DIFFERENT
 * function; loading its parameters anyway produces a network that initializes
 * cleanly, runs at full speed, and plays like noise, with nothing to point at.
 */
export const applyCheckpoint = (net: MlpPolicy, adam: Adam | null, cp: Checkpoint): void => {
  if (cp.version !== CHECKPOINT_VERSION) {
    throw new Error(`checkpoint version ${cp.version}, expected ${CHECKPOINT_VERSION}`);
  }
  if (cp.obsSize !== net.obsSize) {
    throw new Error(`checkpoint obsSize ${cp.obsSize}, network expects ${net.obsSize}`);
  }
  if (cp.actionCount !== net.actionCount) {
    throw new Error(
      `checkpoint actionCount ${cp.actionCount}, network expects ${net.actionCount}`,
    );
  }
  if (
    cp.hiddenSizes.length !== net.hiddenSizes.length ||
    cp.hiddenSizes.some((h, i) => h !== net.hiddenSizes[i])
  ) {
    throw new Error(
      `checkpoint hiddenSizes [${cp.hiddenSizes}], network has [${net.hiddenSizes}]`,
    );
  }
  net.setParameters(cp.parameters);
  if (adam && cp.optimizer) adam.importState(cp.optimizer);
};
